import { Fragment, useRef } from "react";
import { get } from "lodash";
import { Dialog, Transition } from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import { FullScreenLoading } from "components/fullScreenLoading";
import Input from "components/input";
import { Label } from "components/label";
import { useGetTracking } from "routes/ewaiver/hooks/useGetTracking";
import { EwaiverType } from "routes/ewaiver/hooks/useEwaiver";

interface PropType {
  item: EwaiverType;
  isMobile: boolean;
  open: boolean;
  onClose: () => void;
}

export const TrackingModal = ({ item, isMobile, open, onClose }: PropType) => {
  const dialogRef = useRef(null);

  const { loading, data } = useGetTracking(item?.event_id);

  if (loading && open) {
    return <FullScreenLoading />;
  }

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[60]"
        onClose={onClose}
        initialFocus={dialogRef ? dialogRef : undefined}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div
          className="fixed inset-0 z-[60] w-screen overflow-y-auto"
          ref={dialogRef}
        >
          <div className="flex h-dvh items-center justify-center p-4 box-border">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className="relative transform overflow-hidden max-h-full flex flex-col
                            rounded-lg bg-white text-left shadow-xl transition-all
                            w-full md:w-[1240px]"
              >
                <div className="flex items-center justify-between px-[24px] pt-[24px] pb-[16px] rounded-t">
                  <h3 className="text-xl font-semibold text-gray-900">
                    eWaiver Tracking
                  </h3>
                  <span
                    className="bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 border-0 ms-auto inline-flex justify-center items-center ring-transparent cursor-pointer"
                    data-modal-hide="static-modal"
                    onClick={onClose}
                  >
                    <XIcon width={20} height={20} />
                  </span>
                </div>

                <div className="w-[calc(100%-48px)] h-[1px] mx-auto bg-[#C5CCD2]"></div>

                <div
                  className={`text-sm overflow-y-auto px-[24px] pt-[16px] pb-[30px] grid gap-[16px]
                    ${isMobile ? "grid-cols-1" : "grid-cols-2"}`}
                >
                  <div className="w-full flex flex-col gap-[4px]">
                    <Label
                      id="authorized_pick"
                      name="Authorized Pick (Unsigned)"
                    />

                    <Input disabled value={get(data, "authorize_pick")} />
                  </div>

                  <div className="w-full flex flex-col gap-[4px]">
                    <Label id="self_pick" name="Self Pick (Unsigned)" />

                    <Input disabled value={get(data, "self_pick")} />
                  </div>

                  <div className="w-full flex flex-col gap-[4px]">
                    <Label
                      id="online_check_in_count"
                      name="Online Check-in Count"
                    />

                    <Input disabled value={get(data, "online_checkin_count")} />
                  </div>

                  <div className="w-full flex flex-col gap-[4px]">
                    <Label
                      id="self_pick_online_checked"
                      name="Self Pick Online Checked"
                    />

                    <Input
                      disabled
                      value={get(data, "self_pick_online_checked")}
                    />
                  </div>

                  <div className="w-full flex flex-col gap-[4px]">
                    <Label
                      id="authorized_pick_online_checked"
                      name="Authorized Pick Online Checked"
                    />

                    <Input
                      disabled
                      value={get(data, "authorize_pick_online_checked")}
                    />
                  </div>

                  <div className="w-full flex flex-col gap-[4px]">
                    <Label
                      id="unique_link_emails_send"
                      name="# Unique link emails send"
                    />

                    <Input disabled value={get(data, "email_unique_send")} />
                  </div>

                  <div className="w-full flex flex-col gap-[4px]">
                    <Label
                      id="unique_link_emails_sent_successfully"
                      name="# Unique link emails sent successfully"
                    />

                    <Input
                      disabled
                      value={get(data, "email_unique_send_success")}
                    />
                  </div>

                  <div className="w-full flex flex-col gap-[4px]">
                    <Label
                      id="onsite_qr_check_in_emails_sent"
                      name="# Onsite QR check-in emails sent"
                    />

                    <Input disabled value={get(data, "email_qr_code_send")} />
                  </div>

                  <div className="w-full flex flex-col gap-[4px]">
                    <Label
                      id="onsite_qr_check_in_emails_sent_successfully"
                      name="# Onsite QR check-in emails sent successfully"
                    />

                    <Input
                      disabled
                      value={get(data, "email_qr_code_success")}
                    />
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
